import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  DeleteButton,
  EditBase,
  ListBase,
  Pagination,
  RecordContextProvider,
  ResourceContextProvider,
  required,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  useBlocker,
  useCreatePath,
  useEditContext,
  useGetList,
  useListContext,
  useNotify,
  useRecordContext,
  useSaveContext,
  useTranslate,
} from 'react-admin'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'
import { Link, Navigate, useSearchParams } from 'react-router-dom'

import FailedMembershipAlert from './FailedMembershipAlert'
import MembershipColumn from './MembershipColumn'
import { ACCESS_GROUPS_PATH } from './routes'
import useBatchedGetMany from './useBatchedGetMany'
import {
  SYSTEM_PERMISSION_CREATE,
  SYSTEM_PERMISSION_DELETE,
  SYSTEM_PERMISSION_LIST,
  SYSTEM_PERMISSION_READ,
  SYSTEM_PERMISSION_UPDATE,
} from '../../config/permissions'
import { useCommonStyles } from '../../config/theme'
import { useIsFieldPermissionDenied, useResourcePermissions } from '../../domain/permissions'
import { useSmallScreen } from '../../utils/theme'
import { CardTitle } from '../common'
import { AdvancedCreateButton } from '../common/create'
import { ListReference } from '../common/list'
import eventsConfig from '../events/config'
import { EventsListLayout } from '../events/list'
import userGroupsConfig from '../userGroups/config'
import UserGroupFormLayout, { transformValues as transformUserGroup } from '../userGroups/form'
import vehicleGroupsConfig from '../vehicleGroups/config'
import VehicleGroupFormLayout, { transformValues as transformVehicleGroup } from '../vehicleGroups/form'

const groupTypes = {
  'user-groups': {
    config: userGroupsConfig,
    memberSource: 'user_ids',
    memberResource: 'users',
    accessSource: 'vehicle_group_ids',
    accessResource: 'vehicle-groups',
    form: <UserGroupFormLayout />,
    transform: transformUserGroup,
  },
  'vehicle-groups': {
    config: vehicleGroupsConfig,
    memberSource: 'vehicle_ids',
    memberResource: 'vehicles',
    accessSource: 'user_group_ids',
    accessResource: 'user-groups',
    form: <VehicleGroupFormLayout />,
    transform: transformVehicleGroup,
  },
}

const selectedGroupBackground = (theme) => (theme.palette.mode === 'dark' ? '#3d5654' : 'rgba(0, 137, 123, 0.16)')

const getAccessibleMemberCount = (groupType, linkedIds, linkedGroups) => {
  const memberSource = groupTypes[groupType].accessResource === 'vehicle-groups' ? 'vehicle_ids' : 'user_ids'
  const memberIds = new Set()

  for (const id of linkedIds) {
    const group = linkedGroups.get(String(id))
    if (!Array.isArray(group?.[memberSource])) return null
    group[memberSource].forEach((memberId) => memberIds.add(String(memberId)))
  }

  return memberIds.size
}

const translateGroupSummary = (translate, groupType, members, accessibleMembers) => {
  const users = groupType === 'user-groups' ? members : accessibleMembers
  const vehicles = groupType === 'vehicle-groups' ? members : accessibleMembers

  return translate(`mymove.accessGroups.${groupType === 'user-groups' ? 'user' : 'vehicle'}Summary`, {
    smart_count: members,
    users: translate('mymove.accessGroups.userCount', { smart_count: users }),
    vehicles: translate('mymove.accessGroups.vehicleCount', { smart_count: vehicles }),
  })
}

const getGroupSaveChanges = (translate, groupType, data, previousData) => {
  if (!previousData || !data) return []

  const { memberSource, accessSource } = groupTypes[groupType]
  const changes = []
  if (data.name !== previousData.name || data.description !== previousData.description) {
    changes.push(translate('mymove.accessGroups.saveChanges.detailsUpdated'))
  }

  for (const [source, addedKey, removedKey] of [
    [
      memberSource,
      groupType === 'user-groups' ? 'usersAdded' : 'vehiclesAdded',
      groupType === 'user-groups' ? 'usersRemoved' : 'vehiclesRemoved',
    ],
    [
      accessSource,
      groupType === 'user-groups' ? 'vehicleGroupsLinked' : 'userGroupsLinked',
      groupType === 'user-groups' ? 'vehicleGroupsUnlinked' : 'userGroupsUnlinked',
    ],
  ]) {
    if (!Array.isArray(data[source]) || !Array.isArray(previousData[source])) continue
    const before = new Set(previousData[source].map(String))
    const after = new Set(data[source].map(String))
    const added = [...after].filter((id) => !before.has(id)).length
    const removed = [...before].filter((id) => !after.has(id)).length
    if (added) changes.push(translate(`mymove.accessGroups.saveChanges.${addedKey}`, { smart_count: added }))
    if (removed) changes.push(translate(`mymove.accessGroups.saveChanges.${removedKey}`, { smart_count: removed }))
  }

  return changes
}

const GroupList = ({ selectedId, onSelect, groupType, isSmallScreen }) => {
  const { data, isPending, error, refetch, setFilters } = useListContext()
  const translate = useTranslate()
  const [search, setSearch] = useState('')
  const groups = data || []
  const { memberSource, accessSource, accessResource } = groupTypes[groupType]
  const [canReadAccess] = useResourcePermissions(accessResource, SYSTEM_PERMISSION_READ)
  const linkedIds = [...new Set(groups.flatMap((group) => group[accessSource] || []).map(String))]
  const { data: linkedGroups } = useBatchedGetMany(accessResource, linkedIds, {
    enabled: canReadAccess === true && linkedIds.length > 0,
  })
  const linkedGroupsById = new Map(linkedGroups.map((group) => [String(group.id), group]))

  return (
    <Stack sx={{ minWidth: 0 }}>
      <TextField
        label={translate('ra.action.search')}
        value={search}
        onChange={(event) => {
          const value = event.target.value
          setSearch(value)
          setFilters(value ? { name: value } : {})
        }}
        variant="outlined"
        size="small"
        sx={{ m: 0, mb: 1, width: '100%' }}
      />
      {isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} aria-label={translate('ra.page.loading')} />
        </Box>
      ) : error ? (
        <Alert severity="error" action={<Button onClick={() => refetch()}>{translate('ra.action.refresh')}</Button>}>
          {translate('mymove.accessGroups.loadGroupsFailed')}
        </Alert>
      ) : groups.length ? (
        groups.map((group) => {
          const isSelected = !isSmallScreen && String(selectedId) === String(group.id)
          const warnings = []
          if (Array.isArray(group[memberSource]) && group[memberSource].length === 0) {
            warnings.push(translate(`mymove.accessGroups.groupWarnings.${groupType}.noMembers`))
          }
          if (Array.isArray(group[accessSource]) && group[accessSource].length === 0) {
            warnings.push(translate(`mymove.accessGroups.groupWarnings.${groupType}.noAccess`))
          }
          const warning = warnings.join(' ')
          const accessibleMembers =
            canReadAccess && Array.isArray(group[accessSource])
              ? getAccessibleMemberCount(groupType, group[accessSource], linkedGroupsById)
              : null

          return (
            <Button
              key={group.id}
              fullWidth
              variant="text"
              aria-pressed={isSelected}
              onClick={() => onSelect(group.id)}
              sx={{
                justifyContent: 'space-between',
                textAlign: 'left',
                textTransform: 'none',
                borderRadius: 0,
                color: 'text.primary',
                bgcolor: isSelected ? selectedGroupBackground : 'transparent',
                p: isSmallScreen ? 2 : 1.5,
                borderBottom: 1,
                borderBottomColor: 'divider',
                '&:hover': {
                  bgcolor: isSelected ? selectedGroupBackground : 'action.hover',
                },
              }}
            >
              <Stack alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ maxWidth: '100%' }}>
                  {group.name}
                </Typography>
                {Array.isArray(group[memberSource]) && accessibleMembers !== null ? (
                  <Typography variant="caption" color="text.secondary">
                    {translateGroupSummary(translate, groupType, group[memberSource].length, accessibleMembers)}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {group.description}
                  </Typography>
                )}
              </Stack>
              {group.is_default && (
                <Chip size="small" label={translate('mymove.accessGroups.default')} sx={{ ml: 1, flexShrink: 0 }} />
              )}
              {warning && (
                <Tooltip title={warning} arrow>
                  <Box
                    component="span"
                    role="img"
                    aria-label={warning}
                    sx={{ display: 'inline-flex', alignSelf: 'center', ml: 1, flexShrink: 0, color: 'warning.main' }}
                  >
                    <WarningAmberOutlinedIcon fontSize="small" aria-hidden="true" />
                  </Box>
                </Tooltip>
              )}
              {isSmallScreen && <ChevronRightIcon sx={{ color: 'text.secondary', ml: 1, flexShrink: 0 }} />}
            </Button>
          )
        })
      ) : (
        <Typography color="text.secondary">
          {translate(`mymove.accessGroups.${search.trim() ? 'noMatchingGroups' : 'noGroups'}`)}
        </Typography>
      )}
      {!error && <Pagination rowsPerPageOptions={[5]} />}
    </Stack>
  )
}

const GroupToolbar = () => {
  const toolbarRef = useRef(null)
  const [cardBounds, setCardBounds] = useState(null)

  useEffect(() => {
    const card = toolbarRef.current.closest('.MuiCard-root')
    const emulatorWarning = document.querySelector('.firebase-emulator-warning')
    const updateBounds = () => {
      const { left, width } = card.getBoundingClientRect()
      setCardBounds({ left, width, bottom: emulatorWarning?.getBoundingClientRect().height || 0 })
    }
    const observer = new ResizeObserver(updateBounds)
    observer.observe(card)
    if (emulatorWarning) observer.observe(emulatorWarning)
    window.addEventListener('resize', updateBounds)
    document.addEventListener('transitionend', updateBounds)
    updateBounds()

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateBounds)
      document.removeEventListener('transitionend', updateBounds)
    }
  }, [])

  return (
    <Box
      ref={toolbarRef}
      sx={{
        position: { sm: cardBounds ? 'fixed' : 'static' },
        bottom: cardBounds?.bottom || 0,
        left: { sm: cardBounds?.left },
        zIndex: 1,
        width: { sm: cardBounds?.width },
      }}
    >
      <Toolbar
        sx={{
          '&.RaToolbar-mobileToolbar': { bottom: cardBounds?.bottom || 0 },
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <SaveButton />
      </Toolbar>
    </Box>
  )
}

const GroupDeleteButton = ({ groupType, canDelete, onMembershipError }) => {
  const record = useRecordContext()
  const notify = useNotify()

  if (!canDelete || !record || record.is_default) return null

  return (
    <DeleteButton
      mutationMode="pessimistic"
      redirect={`${ACCESS_GROUPS_PATH}?tab=${groupType}`}
      mutationOptions={{
        onError: (error) => {
          if (error.code === 'last_group_membership' && record[groupTypes[groupType].memberSource]?.length) {
            onMembershipError()
          } else {
            notify(error.message, { type: 'error' })
          }
        },
      }}
    />
  )
}

const GroupCount = ({ groupType }) => {
  const translate = useTranslate()
  const { memberSource, accessSource, accessResource } = groupTypes[groupType]
  const [canReadAccess] = useResourcePermissions(accessResource, SYSTEM_PERMISSION_READ)
  const members = useWatch({ name: memberSource }) || []
  const linkedIds = useWatch({ name: accessSource }) || []
  const { data: linkedGroups } = useBatchedGetMany(accessResource, linkedIds, {
    enabled: canReadAccess === true && linkedIds.length > 0,
  })
  const linkedGroupsById = new Map(linkedGroups.map((group) => [String(group.id), group]))
  const accessibleMembers = canReadAccess ? getAccessibleMemberCount(groupType, linkedIds, linkedGroupsById) : null

  return (
    <Typography variant="body2" color="text.secondary">
      {accessibleMembers === null
        ? null
        : translateGroupSummary(translate, groupType, members.length, accessibleMembers)}
    </Typography>
  )
}

const RestoreGroupAfterFailedSave = ({ failedSaveCount }) => {
  const { record, refetch } = useEditContext()
  const { reset } = useFormContext()
  const recordRef = useRef(record)
  recordRef.current = record

  useEffect(() => {
    if (!failedSaveCount) return

    let active = true
    reset(recordRef.current)
    refetch().then(({ data }) => {
      if (active && data) reset(data)
    })

    return () => {
      active = false
    }
  }, [failedSaveCount, refetch, reset])

  return null
}

const GroupLeavePrompt = ({ onSaveSuccess, onSaveError }) => {
  const translate = useTranslate()
  const { handleSubmit, setError } = useFormContext()
  const { dirtyFields } = useFormState()
  const { save } = useSaveContext()
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const isDirty = Object.keys(dirtyFields).length > 0
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search ||
        currentLocation.hash !== nextLocation.hash),
  )

  useEffect(() => {
    if (blocker.state !== 'blocked') setSaveError(false)
  }, [blocker.state])

  useEffect(() => {
    if (!isDirty) return
    const beforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = true
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [isDirty])

  const saveAndLeave = handleSubmit(
    async (values) => {
      setSaving(true)
      setSaveError(false)
      try {
        const errors = await save(values, {
          onSuccess: (...args) => {
            onSaveSuccess(...args)
            blocker.proceed?.()
          },
          onError: (...args) => {
            setSaveError(true)
            onSaveError(...args)
          },
        })
        if (errors) {
          setSaveError(true)
          Object.entries(errors).forEach(([source, message]) => setError(source, { type: 'server', message }))
        }
      } catch (error) {
        setSaveError(true)
        onSaveError(error)
      } finally {
        setSaving(false)
      }
    },
    () => setSaveError(true),
  )

  return (
    <Dialog open={blocker.state === 'blocked'} onClose={() => !saving && blocker.proceed?.()}>
      <DialogTitle>{translate('mymove.accessGroups.saveBeforeLeavingTitle')}</DialogTitle>
      <DialogContent>
        {translate('mymove.accessGroups.saveBeforeLeavingMessage')}
        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {translate('mymove.accessGroups.saveBeforeLeavingFailed')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {saveError && <Button onClick={() => blocker.reset?.()}>{translate('mymove.accessGroups.keepEditing')}</Button>}
        <Button disabled={saving} onClick={() => blocker.proceed?.()}>
          {translate('mymove.accessGroups.leaveWithoutSaving')}
        </Button>
        <Button variant="contained" disabled={saving} onClick={saveAndLeave}>
          {saving ? <CircularProgress size={20} color="inherit" /> : translate('mymove.accessGroups.saveAndLeave')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const GroupEditor = ({ id, groupType, onBack, onSelectGroup }) => {
  const { memberSource, memberResource, accessSource, accessResource, transform } = groupTypes[groupType]
  const queryClient = useQueryClient()
  const translate = useTranslate()
  const notify = useNotify()
  const [failedSaveCount, setFailedSaveCount] = useState(0)
  const [failedRemovalIds, setFailedRemovalIds] = useState(null)
  const [canUpdate] = useResourcePermissions(groupType, SYSTEM_PERMISSION_UPDATE)
  const [canDelete] = useResourcePermissions(groupType, SYSTEM_PERMISSION_DELETE)
  const [canReadMembers] = useResourcePermissions(memberResource, SYSTEM_PERMISSION_READ)
  const [canReadAccess] = useResourcePermissions(accessResource, SYSTEM_PERMISSION_READ)
  const onSaveSuccess = (data, { previousData } = {}) => {
    setFailedRemovalIds(null)
    setFailedSaveCount(0)
    queryClient.invalidateQueries({ queryKey: [eventsConfig.name, 'getManyReference'] })
    const changes = getGroupSaveChanges(translate, groupType, data, previousData)
    notify(`resources.${groupType}.notifications.${changes.length ? 'updatedWithChanges' : 'updated'}`, {
      type: 'info',
      messageArgs: { name: data.name, changes: changes.join(', ') },
    })
  }
  const onSaveError = (error, { data, previousData } = {}) => {
    if (error.code === 'last_group_membership') {
      const submittedIds = new Set((data?.[memberSource] || []).map(String))
      setFailedRemovalIds(
        (previousData?.[memberSource] || []).filter((memberId) => !submittedIds.has(String(memberId))),
      )
    } else {
      setFailedRemovalIds(null)
      notify(error.message, { type: 'error' })
    }
    setFailedSaveCount((count) => count + 1)
  }

  return (
    <EditBase
      id={id}
      resource={groupType}
      mutationMode="pessimistic"
      transform={transform}
      redirect={false}
      mutationOptions={{ onSuccess: onSaveSuccess, onError: onSaveError }}
    >
      <GroupEditorForm
        key={`${groupType}-${id}`}
        groupType={groupType}
        memberSource={memberSource}
        memberResource={memberResource}
        accessSource={accessSource}
        accessResource={accessResource}
        canUpdate={canUpdate}
        canDelete={canDelete}
        canReadMembers={canReadMembers}
        canReadAccess={canReadAccess}
        translate={translate}
        failedSaveCount={failedSaveCount}
        failedRemovalIds={failedRemovalIds}
        onSaveSuccess={onSaveSuccess}
        onSaveError={onSaveError}
        onRemoved={() => setFailedRemovalIds(null)}
        onBack={onBack}
        onSelectGroup={onSelectGroup}
      />
    </EditBase>
  )
}

const GroupEditorForm = ({
  groupType,
  memberSource,
  memberResource,
  accessSource,
  accessResource,
  canUpdate,
  canDelete,
  canReadMembers,
  canReadAccess,
  translate,
  failedSaveCount,
  failedRemovalIds,
  onSaveSuccess,
  onSaveError,
  onRemoved,
  onBack,
  onSelectGroup,
}) => {
  const isSmallScreen = useSmallScreen()
  const [mobileSection, setMobileSection] = useState(canReadMembers ? 'members' : 'access')
  const [editingDetails, setEditingDetails] = useState(false)
  const [failedDeletionCount, setFailedDeletionCount] = useState(0)
  const record = useRecordContext()
  const membersDenied = useIsFieldPermissionDenied({ action: SYSTEM_PERMISSION_UPDATE, source: memberSource })
  const accessDenied = useIsFieldPermissionDenied({ action: SYSTEM_PERMISSION_UPDATE, source: accessSource })
  const nameDenied = useIsFieldPermissionDenied({ action: SYSTEM_PERMISSION_UPDATE, source: 'name' })
  const descriptionDenied = useIsFieldPermissionDenied({ action: SYSTEM_PERMISSION_UPDATE, source: 'description' })
  if (!record) return null

  return (
    <SimpleForm toolbar={canUpdate ? <GroupToolbar /> : false} sx={{ px: 2, pt: 2, pb: { sm: 10 } }}>
      <GroupLeavePrompt onSaveSuccess={onSaveSuccess} onSaveError={onSaveError} />
      <RestoreGroupAfterFailedSave failedSaveCount={failedSaveCount} />
      {failedRemovalIds && (
        <FailedMembershipAlert
          key={failedSaveCount}
          groupId={record.id}
          groupName={record.name}
          memberResource={memberResource}
          removedIds={failedRemovalIds}
          canReadMembers={canReadMembers}
          canAssignMembers={canUpdate && !membersDenied}
          onRemoved={onRemoved}
        />
      )}
      {failedDeletionCount > 0 && (
        <FailedMembershipAlert
          key={`delete-${failedDeletionCount}`}
          groupId={record.id}
          groupName={record.name}
          memberResource={memberResource}
          removedIds={record[memberSource] || []}
          canReadMembers={canReadMembers}
          canAssignMembers={canUpdate && !membersDenied}
          deleting
        />
      )}
      <Stack spacing={1} sx={{ width: '100%' }}>
        {isSmallScreen && editingDetails ? (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setEditingDetails(false)}
            sx={{ alignSelf: 'flex-start' }}
          >
            {translate('mymove.accessGroups.backToGroup')}
          </Button>
        ) : (
          <>
            {isSmallScreen && (
              <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ alignSelf: 'flex-start' }}>
                {translate('mymove.accessGroups.backToGroups')}
              </Button>
            )}
            <Stack direction="row" spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ width: '100%' }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 1, md: 2 }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: { md: '1 1 0%' }, minWidth: 0 }}>
                  <Typography variant="h5" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {record.name}
                  </Typography>
                  {record.is_default && (
                    <Chip size="small" label={translate('mymove.accessGroups.default')} sx={{ flexShrink: 0 }} />
                  )}
                </Stack>
                <Typography color="text.secondary" sx={{ flex: { md: '2 1 0%' }, minWidth: 0 }}>
                  {record.description}
                </Typography>
              </Stack>
              <GroupDeleteButton
                groupType={groupType}
                canDelete={canDelete}
                onMembershipError={() => setFailedDeletionCount((count) => count + 1)}
              />
            </Stack>
            <GroupCount groupType={groupType} />
            {isSmallScreen && canUpdate && (
              <Button onClick={() => setEditingDetails(true)} sx={{ alignSelf: 'flex-start' }}>
                {translate('mymove.accessGroups.editDetails')}
              </Button>
            )}
            {isSmallScreen && (
              <Tabs value={mobileSection} onChange={(_, section) => setMobileSection(section)} variant="fullWidth">
                {canReadMembers && <Tab value="members" label={translate('mymove.accessGroups.members')} />}
                {canReadAccess && <Tab value="access" label={translate('mymove.accessGroups.access')} />}
              </Tabs>
            )}
            <Stack
              direction="row"
              spacing={2}
              sx={{ pt: 2 }}
              divider={!isSmallScreen && <Divider orientation="vertical" flexItem />}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: isSmallScreen && mobileSection !== 'members' ? 'none' : undefined,
                }}
              >
                {canReadMembers && (
                  <MembershipColumn
                    source={memberSource}
                    reference={memberResource}
                    label={translate(`resources.${groupType}.fields.${memberSource}`)}
                    canEdit={canUpdate && !membersDenied}
                    searchable
                    onSelectGroup={onSelectGroup}
                  />
                )}
              </Box>
              <Box
                sx={{ flex: 1, minWidth: 0, display: isSmallScreen && mobileSection !== 'access' ? 'none' : undefined }}
              >
                {canReadAccess && (
                  <MembershipColumn
                    source={accessSource}
                    reference={accessResource}
                    label={translate(`resources.${groupType}.fields.${accessSource}`)}
                    canEdit={canUpdate && !accessDenied}
                    onSelectGroup={onSelectGroup}
                  />
                )}
              </Box>
            </Stack>
          </>
        )}
        {canUpdate && (!isSmallScreen || editingDetails) && (
          <Box sx={{ width: '100%', pt: 4 }}>
            <Box sx={{ px: 2, py: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {translate('mymove.accessGroups.editDetails')}
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextInput
                  source="name"
                  readOnly={record.is_default || nameDenied}
                  validate={required()}
                  fullWidth
                  sx={{ flex: { md: '1 1 0%' }, minWidth: 0 }}
                />
                <TextInput
                  source="description"
                  readOnly={descriptionDenied}
                  validate={required()}
                  fullWidth
                  sx={{ flex: { md: '2 1 0%' }, minWidth: 0 }}
                />
              </Stack>
            </Box>
          </Box>
        )}
      </Stack>
    </SimpleForm>
  )
}

const GroupTab = ({
  groupType,
  selectedId,
  onSelect,
  canViewUsers,
  canViewVehicles,
  onSwitchTab,
  onSelectGroup,
  showExplanation,
  onToggleExplanation,
}) => {
  const { config, form, transform } = groupTypes[groupType]
  const translate = useTranslate()
  const isSmallScreen = useSmallScreen()
  const createPath = useCreatePath()
  const [canCreate] = useResourcePermissions(groupType, SYSTEM_PERMISSION_CREATE)
  const commonClasses = useCommonStyles()
  const initialSelectionDone = useRef(Boolean(selectedId))
  const {
    data: defaultGroups,
    isPending: defaultPending,
    error: defaultError,
    refetch: refetchDefault,
  } = useGetList(
    groupType,
    {
      pagination: { page: 1, perPage: 1 },
      sort: config.options.defaultSort,
      filter: { is_default: true },
    },
    { enabled: !isSmallScreen && !initialSelectionDone.current && !selectedId },
  )

  useEffect(() => {
    const defaultGroup = defaultGroups?.find((group) => group.is_default)
    if (!isSmallScreen && !initialSelectionDone.current && !selectedId && defaultGroup) {
      initialSelectionDone.current = true
      onSelect(defaultGroup.id)
    }
  }, [defaultGroups, isSmallScreen, onSelect, selectedId])

  const selectGroup = (id) => {
    initialSelectionDone.current = true
    onSelect(id)
  }

  return (
    <ResourceContextProvider value={groupType}>
      <ListBase
        resource={groupType}
        perPage={5}
        sort={config.options.defaultSort}
        storeKey={false}
        disableSyncWithLocation
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ mt: isSmallScreen ? 0 : 2, alignItems: 'flex-start' }}
        >
          {(!isSmallScreen || !selectedId) && (
            <Card sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
              {isSmallScreen && (
                <>
                  <CardHeader
                    avatar={
                      <IconButton
                        aria-label={translate('mymove.accessGroups.defaultInfo')}
                        onClick={onToggleExplanation}
                      >
                        <InfoOutlinedIcon />
                      </IconButton>
                    }
                    title={<CardTitle text="mymove.accessGroups.title" />}
                    sx={{ borderBottom: 1, borderColor: 'divider', ...commonClasses.titleContainer }}
                  />
                  {showExplanation && (
                    <Alert severity="info" onClose={onToggleExplanation} sx={{ m: 2 }}>
                      {translate('mymove.accessGroups.defaultExplanation')}
                    </Alert>
                  )}
                  <Tabs value={groupType} onChange={(_, tab) => onSwitchTab(tab)} variant="fullWidth">
                    {canViewUsers && <Tab value="user-groups" label={translate('resources.user-groups.name', 2)} />}
                    {canViewVehicles && (
                      <Tab value="vehicle-groups" label={translate('resources.vehicle-groups.name', 2)} />
                    )}
                  </Tabs>
                </>
              )}
              <CardContent>
                <Stack spacing={2}>
                  {isSmallScreen && canCreate && (
                    <Button
                      component={Link}
                      to={{
                        pathname: createPath({ resource: groupType, type: 'create' }),
                        search: '?' + new URLSearchParams({ defaultValues: '{}' }),
                      }}
                      variant="outlined"
                      fullWidth
                      startIcon={<AddIcon />}
                    >
                      {translate('mymove.accessGroups.newGroup')}
                    </Button>
                  )}
                  <GroupList
                    selectedId={selectedId}
                    onSelect={selectGroup}
                    groupType={groupType}
                    isSmallScreen={isSmallScreen}
                  />
                  {!isSmallScreen && <AdvancedCreateButton transform={transform}>{form}</AdvancedCreateButton>}
                </Stack>
              </CardContent>
            </Card>
          )}
          {(!isSmallScreen || selectedId) && (
            <Stack sx={{ flex: 1, minWidth: 0, width: { xs: '100%', md: 'auto' } }} spacing={1}>
              <Card>
                {selectedId ? (
                  <GroupEditor
                    key={`${groupType}-${selectedId}`}
                    id={selectedId}
                    groupType={groupType}
                    onBack={() => selectGroup(null)}
                    onSelectGroup={onSelectGroup}
                  />
                ) : defaultPending && !initialSelectionDone.current ? (
                  <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} aria-label={translate('ra.page.loading')} />
                  </CardContent>
                ) : defaultError && !initialSelectionDone.current ? (
                  <CardContent>
                    <Alert
                      severity="error"
                      action={<Button onClick={() => refetchDefault()}>{translate('ra.action.refresh')}</Button>}
                    >
                      {translate('mymove.accessGroups.loadGroupsFailed')}
                    </Alert>
                  </CardContent>
                ) : (
                  <CardContent>
                    <Typography color="text.secondary">{translate('mymove.accessGroups.selectGroup')}</Typography>
                  </CardContent>
                )}
              </Card>
              {selectedId && (
                <RecordContextProvider value={{ id: selectedId }}>
                  <ListReference
                    key={selectedId}
                    reference={eventsConfig.name}
                    target="entity_id"
                    sort={eventsConfig.options.defaultSort}
                  >
                    <EventsListLayout excludedFields={['entity_id']} />
                  </ListReference>
                </RecordContextProvider>
              )}
            </Stack>
          )}
        </Stack>
      </ListBase>
    </ResourceContextProvider>
  )
}

export const AccessGroupsPage = () => {
  const translate = useTranslate()
  const isSmallScreen = useSmallScreen()
  const [showExplanation, setShowExplanation] = useState(false)
  const [showDesktopExplanation, setShowDesktopExplanation] = useState(true)
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedGroups = useRef({})
  const [canReadUsers, canListUsers] = useResourcePermissions(
    'user-groups',
    SYSTEM_PERMISSION_READ,
    SYSTEM_PERMISSION_LIST,
  )
  const [canReadVehicles, canListVehicles] = useResourcePermissions(
    'vehicle-groups',
    SYSTEM_PERMISSION_READ,
    SYSTEM_PERMISSION_LIST,
  )
  const canViewUsers = canReadUsers && canListUsers
  const canViewVehicles = canReadVehicles && canListVehicles
  const groupType = searchParams.get('tab') === 'vehicle-groups' ? 'vehicle-groups' : 'user-groups'
  const selectedId = searchParams.get('id')
  const availableGroupType =
    groupType === 'user-groups'
      ? canViewUsers
        ? groupType
        : 'vehicle-groups'
      : canViewVehicles
        ? groupType
        : 'user-groups'

  useEffect(() => {
    if (selectedId) selectedGroups.current[groupType] = selectedId
  }, [groupType, selectedId])

  const switchTab = (tab) => {
    const id = !isSmallScreen && selectedGroups.current[tab]
    setSearchParams(id ? { tab, id } : { tab })
  }

  if (canListUsers === undefined || canListVehicles === undefined) return null
  if (!canViewUsers && !canViewVehicles) return <Navigate to="/" replace />

  return (
    <Box sx={{ p: { xs: 0, md: 2 } }}>
      {!isSmallScreen && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Typography variant="h4">{translate('mymove.accessGroups.title')}</Typography>
          </Stack>
          {showDesktopExplanation && (
            <Alert severity="info" onClose={() => setShowDesktopExplanation(false)} sx={{ mb: 2 }}>
              {translate('mymove.accessGroups.defaultExplanation')}
            </Alert>
          )}
          <Tabs
            value={availableGroupType}
            onChange={(_, tab) => switchTab(tab)}
            aria-label={translate('mymove.accessGroups.title')}
          >
            {canViewUsers && <Tab value="user-groups" label={translate('resources.user-groups.name', 2)} />}
            {canViewVehicles && <Tab value="vehicle-groups" label={translate('resources.vehicle-groups.name', 2)} />}
          </Tabs>
        </>
      )}
      <GroupTab
        key={availableGroupType}
        groupType={availableGroupType}
        selectedId={selectedId}
        canViewUsers={canViewUsers}
        canViewVehicles={canViewVehicles}
        showExplanation={showExplanation}
        onToggleExplanation={() => setShowExplanation(!showExplanation)}
        onSwitchTab={switchTab}
        onSelectGroup={(tab, id) => {
          selectedGroups.current[tab] = String(id)
          setSearchParams({ tab, id: String(id) })
        }}
        onSelect={(id) => {
          if (id) selectedGroups.current[availableGroupType] = String(id)
          setSearchParams(id ? { tab: availableGroupType, id: String(id) } : { tab: availableGroupType })
        }}
      />
    </Box>
  )
}

AccessGroupsPage.path = ACCESS_GROUPS_PATH
